import { useMemo } from 'react';
import { type AssignmentDto, aggregateRange } from '@betreuung/shared';
import { SwatchLabel } from '@/components/shared/Swatch';
import type { PersonLookup } from '@/lib/persons';
import { cn } from '@/lib/utils';

export interface MonthLegendProps {
  assignments: readonly AssignmentDto[];
  persons: PersonLookup;
  /** The visible calendar month, not the padded grid range. */
  fromSlot: number;
  toSlot: number;
  className?: string;
}

/**
 * Swatch legend of the visible month. Only people who actually appear in the
 * month are listed, so the legend stays a key to the colours on screen instead
 * of a person directory.
 */
export function MonthLegend({
  assignments,
  persons,
  fromSlot,
  toSlot,
  className,
}: MonthLegendProps) {
  const { present, hasUnassigned } = useMemo(() => {
    const { rows } = aggregateRange(assignments, fromSlot, toSlot);
    const ids = new Set<number>();
    let unassigned = false;
    for (const row of rows) {
      if (row.slots === 0) continue;
      if (row.personId === null) unassigned = true;
      else ids.add(row.personId);
    }
    return { present: ids, hasUnassigned: unassigned };
  }, [assignments, fromSlot, toSlot]);

  const entries = persons.all.filter((person) => present.has(person.id));
  if (entries.length === 0 && !hasUnassigned) return null;

  return (
    <div className={cn('flex flex-wrap gap-x-3 gap-y-1.5 px-4 pt-2', className)}>
      {entries.map((person) => (
        <SwatchLabel key={person.id} colorIndex={person.colorIndex} name={person.name} />
      ))}
      {hasUnassigned ? <SwatchLabel colorIndex={null} /> : null}
    </div>
  );
}
